import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';

import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import { allTravelDnaSteps } from '@/_constants/TravelDnaStep.ts';
import TravelDnaStep from '@/_constants/TravelDnaStep.ts';
import { dnaSchema } from '$/requests/UpsertDnaRequest.ts';
import YourApproachToFoodWhenYouTravel from '$/_const/dna/YourApproachToFoodWhenYouTravel.ts';
import type { YourApproachToFoodWhenYouTravel as TableOption } from '$/_const/dna/YourApproachToFoodWhenYouTravel.ts';
import type { TravelDnaFormData } from '$/models/Dna.d.ts';
import { missingTranslationToReadable } from '@/utils/missingTranslationToReadable.ts';
import type { OnCompleteArgs } from '@/components/functional/TravelDnaManipulationForm/TravelDnaManipulationForm.tsx';
import DnaStepLayout from '@/components/functional/TravelDnaManipulationForm/shared/DnaStepLayout.tsx';
import DnaImageChoiceCard from '@/components/functional/TravelDnaManipulationForm/shared/DnaImageChoiceCard.tsx';
import DnaError from '@/components/functional/TravelDnaManipulationForm/shared/DnaError.tsx';

import localFavouritesImage from '@/assets/img/onboarding/food/local_favourites.jpg';
import newAndLivelyRestaurantsImage from '@/assets/img/onboarding/food/new_and_lively_restaurants.jpg';
import specialOccasionDiningImage from '@/assets/img/onboarding/food/special_occasion_dining.jpg';
import streetFoodImage from '@/assets/img/onboarding/food/street_food.jpg';

const yourTableSchema = dnaSchema.pick({
  your_approach_to_food_when_you_travel: true,
});

export type YourTableSchema = z.infer<typeof yourTableSchema>;

type YourTableProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

const tableOptions: TableOption[] = [
  YourApproachToFoodWhenYouTravel.STREET_FOOD_AND_CASUAL_SPOTS,
  YourApproachToFoodWhenYouTravel.LOCAL_FAVOURITES,
  YourApproachToFoodWhenYouTravel.NEW_AND_LIVELY_RESTAURANTS,
  YourApproachToFoodWhenYouTravel.SPECIAL_OCCASION_DINING,
];

const tableImages: Record<TableOption, string> = {
  street_food_and_casual_spots: streetFoodImage,
  local_favourites: localFavouritesImage,
  new_and_lively_restaurants: newAndLivelyRestaurantsImage,
  special_occasion_dining: specialOccasionDiningImage,
  food_is_not_a_priority: streetFoodImage,
  local_and_authentic: localFavouritesImage,
  fine_dining: specialOccasionDiningImage,
  a_mix_of_local_authentic_and_fine_dining: newAndLivelyRestaurantsImage,
};

function YourTable({ onComplete, onCompleteWhenDirty }: YourTableProps) {
  const { t } = useTranslation();
  const { nextStep, setContextData, contextData } = useStepperContext<
    typeof allTravelDnaSteps,
    TravelDnaFormData
  >();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const selectedValue = contextData?.your_approach_to_food_when_you_travel;

  const selectOption = (option: TableOption) => {
    void (async () => {
      try {
        if (isPending) return;
        setIsPending(true);
        setError(null);
        const data = yourTableSchema.parse({
          your_approach_to_food_when_you_travel: option,
        });
        const context = { ...contextData, ...data };
        const args: OnCompleteArgs = {
          step: TravelDnaStep.YOUR_TABLE,
          data,
          context,
        };

        if (selectedValue !== option) {
          setContextData((previous) => ({ ...previous, ...data }));
          await onCompleteWhenDirty?.(args);
        }

        await onComplete?.(args);
        nextStep();
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError))
        );
      } finally {
        setIsPending(false);
      }
    })();
  };

  const selectFoodNotPriority = () => {
    selectOption(YourApproachToFoodWhenYouTravel.FOOD_IS_NOT_A_PRIORITY);
  };

  return (
    <DnaStepLayout
      eyebrow={t('travelDnaPage.yourTable.header')}
      title={t('travelDnaPage.yourTable.subheader')}
      description={t('travelDnaPage.yourTable.description')}
    >
      <DnaError error={error} className="mt-6" />
      <div className="mt-6 grid w-full grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-x-12 sm:gap-y-7">
        {tableOptions.map((option) => {
          const selected = selectedValue === option;
          return (
            <DnaImageChoiceCard
              key={option}
              image={tableImages[option]}
              label={t(
                `travelDnaPage.yourTable.options.${option}`,
                missingTranslationToReadable(option)
              )}
              selected={selected}
              dimmed={Boolean(selectedValue) && !selected}
              disabled={isPending}
              onClick={() => selectOption(option)}
            />
          );
        })}
      </div>

      <button
        type="button"
        disabled={isPending}
        onClick={selectFoodNotPriority}
        className={`mt-5 w-full rounded-[10px] border px-6 py-3 font-sans text-lg italic transition-all duration-300 ${
          selectedValue === YourApproachToFoodWhenYouTravel.FOOD_IS_NOT_A_PRIORITY
            ? 'border-[var(--gold)] bg-[var(--gold-lt)] text-[var(--ink)]'
            : 'border-[var(--gold-deep)] bg-[var(--gold)] text-[var(--ink)]'
        }`}
      >
        {t('travelDnaPage.yourTable.options.food_is_not_a_priority')}
      </button>
    </DnaStepLayout>
  );
}

export default YourTable;
